import React, { useState } from 'react';
import { TextField } from '@mui/material';
import { ConfirmDialog } from '../../reports/edit/EditCommon';
import { normalizeName, usageText, validateRegionName } from './regionsCalc';
import type { Region } from './regionsApi';

interface RegionFormDialogProps {
  open: boolean;
  /** Редактируемый регион; null - добавление нового. Форма заполняется при монтировании: на каждое открытие свой key */
  target: Region | null;
  regions: Region[];
  saving: boolean;
  /** Возвращает true, если регион сохранен и форму можно закрыть */
  onSave: (target: Region | null, name: string) => Promise<boolean>;
  onClose: () => void;
}

const RegionFormDialog: React.FC<RegionFormDialogProps> = ({ open, target, regions, saving, onSave, onClose }) => {
  const isEditing = target !== null;
  const [name, setName] = useState(target ? target.name : '');
  const [error, setError] = useState('');

  const used = target ? usageText(target) : '';

  const submit = async () => {
    if (saving) return;
    const found = validateRegionName(name, regions, target ? target.id : null);
    setError(found);
    if (found) return;

    const clean = normalizeName(name);
    // Название не поменялось: сохранять нечего
    if (target && clean === target.name) {
      onClose();
      return;
    }
    if (await onSave(target, clean)) onClose();
  };

  // Enter в поле сохраняет форму, как кнопка
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submit();
    }
  };

  return (
    <ConfirmDialog
      open={open}
      title={isEditing ? 'Переименовать регион' : 'Добавить регион'}
      confirmText={isEditing ? 'Сохранить' : 'Добавить'}
      loading={saving}
      maxWidth="xs"
      onConfirm={submit}
      onClose={onClose}
    >
      <TextField
        size="small"
        fullWidth
        autoFocus
        required
        label="Название региона"
        value={name}
        onChange={(event) => {
          setName(event.target.value);
          setError('');
        }}
        onKeyDown={handleKeyDown}
        error={!!error}
        helperText={
          error || (used ? `Новое название получат привязанные записи (${used})` : 'Так регион виден в профилях сотрудников и терминалах')
        }
      />
    </ConfirmDialog>
  );
};

export default RegionFormDialog;
